import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

class DateService {
    private readonly timeZone: string = 'Asia/Singapore';



    private readonly dateTimeFormat: string = 'YYYY-MM-DDTHH:mm:ss';

    getToday(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getYesterday(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).subtract(1, 'day').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).subtract(1, 'day').endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getThisWeek(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).startOf('isoWeek').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).endOf('isoWeek').endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getLastWeek(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).subtract(1, 'week').startOf('isoWeek').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).subtract(1, 'week').endOf('isoWeek').endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getLast7Days(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).subtract(7, 'day').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getThisMonth(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).startOf('month').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).endOf('month').endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getLastMonth(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).subtract(1, 'month').startOf('month').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).subtract(1, 'month').endOf('month').endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getThisYear(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).startOf('year').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).endOf('year').endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    getLastYear(): { startDate: string; endDate: string } {
        const startDate = dayjs().tz(this.timeZone).subtract(1, 'year').startOf('year').startOf('day').format(this.dateTimeFormat);
        const endDate = dayjs().tz(this.timeZone).subtract(1, 'year').endOf('year').endOf('day').format(this.dateTimeFormat);
        return { startDate, endDate };
    }

    formatDate(date: Dayjs | null) {
        return date ? date.tz(this.timeZone).format('YYYY-MM-DD') : undefined;
    }

    formatStartOfDay(date: Dayjs | null, time?: string | null): string | undefined {
        if (!date) return undefined;
        const d = date.tz(this.timeZone);
        if (time) {
            const [hours, minutes] = time.split(':').map(Number);
            return d.hour(hours).minute(minutes).second(0).format(this.dateTimeFormat);
        }
        return d.startOf('day').format(this.dateTimeFormat);
    }

    formatEndOfDay(date: Dayjs | null, time?: string | null): string | undefined {
        if (!date) return undefined;
        const d = date.tz(this.timeZone);
        if (time) {
            const [hours, minutes] = time.split(':').map(Number);
            return d.hour(hours).minute(minutes).second(59).format(this.dateTimeFormat);
        }
        return d.endOf('day').format(this.dateTimeFormat);
    }

    currentDate() {
        return dayjs().tz(this.timeZone).format('DD-MM-YYYY');
    }

    currentDateTime() {
        return dayjs().tz(this.timeZone).format('DD-MM-YYYY, HH:mm');
    }

    currentDayNumber() {
        return dayjs().tz(this.timeZone).date();
    }

    formatDateForTable(date: string): string {
        const dateObj = new Date(date);

        const hours = dateObj.getHours().toString().padStart(2, '0');
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const day = dateObj.getDate().toString().padStart(2, '0');
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const year = dateObj.getFullYear();

        return `${day}.${month}.${year}, ${hours}:${minutes} `;
    }

    formatDateTime(date: Dayjs | null, time: string | null): string | undefined {
        if (!date || !time) return undefined;

        const [hours, minutes] = time.split(':').map(Number);

        const dateTime = date
            .tz(this.timeZone)
            .hour(hours)
            .minute(minutes)
            .second(0)
            .millisecond(0);

        return dateTime.format('YYYY-MM-DDTHH:mm:ss');
    }
}

const dateService = new DateService;

export default dateService;
